import type { Profile } from "@kedaat/engine";

/**
 * Starting profiles. None is verified yet (SPEC 10.3): every profile must be checked
 * against its source luach and signed off by a rav before launch.
 */
export const PROFILES: readonly Profile[] = [
  {
    id: "gra",
    label: { he: 'גר"א', en: "GRA" },
    description: {
      he: "שעות זמניות מהנץ עד השקיעה, צאת הכוכבים 8.5°. נץ ושקיעה מישוריים.",
      en: "Temporal hours from sunrise to sunset, nightfall at 8.5°. Sea-level sunrise and sunset.",
    },
    status: "draft",
    openQuestions: [
      {
        he: "האם צאת הכוכבים לימות החול צריכה להיות 8.5° (שלושה כוכבים קטנים), או זמן מוקדם יותר כמו 7.083° או 18 דקות?",
        en: "Should weekday nightfall be 8.5 degrees (three small stars), or an earlier time such as 7.083 degrees or 18 minutes?",
      },
      {
        he: "האם משיכיר 11° הוא הזמן הנכון לפרופיל זה, או 11.5°?",
        en: "Is misheyakir at 11 degrees right for this profile, or 11.5 degrees?",
      },
      {
        he: "האם צאת הכוכבים לרבנו תם צריכה להיות 72 דקות קבועות או 72 דקות זמניות?",
        en: "Should Rabbeinu Tam be 72 fixed minutes or 72 temporal minutes?",
      },
    ],
    opinions: {
      alos: "alos.deg_16_1",
      misheyakir: "misheyakir.deg_11",
      sunrise: "sunrise.sea_level",
      sof_zman_shma: "sof_zman_shma.gra",
      sof_zman_tfila: "sof_zman_tfila.gra",
      chatzot: "chatzot.astronomical",
      mincha_gedola: "mincha_gedola.gra_greater_than_30",
      mincha_ketana: "mincha_ketana.gra",
      plag_hamincha: "plag_hamincha.gra",
      sunset: "sunset.sea_level",
      tzeit: "tzeit.deg_8_5",
      tzeit_rt: "tzeit_rt.min_72",
      chatzot_night: "chatzot_night.half_night",
      sof_zman_achilat_chametz: "sof_zman_achilat_chametz.gra",
      sof_zman_biur_chametz: "sof_zman_biur_chametz.gra",
    },
  },
  {
    id: "mga",
    label: { he: "מגן אברהם", en: "Magen Avraham" },
    description: {
      he: 'סוף זמן קריאת שמע ותפילה לפי מג"א: היום מעלות השחר (72 דקות) עד צאת הכוכבים (72 דקות). שאר הזמנים כגר"א.',
      en: "Latest Shema and Shacharit per Magen Avraham, with the day from dawn (72 minutes) to nightfall (72 minutes). Other zmanim as GRA.",
    },
    status: "draft",
    openQuestions: [
      {
        he: "האם עלות השחר צריכה להיות 72 דקות קבועות או 16.1°, ולפי איזה מהם נמנות השעות הזמניות?",
        en: "Should dawn be 72 fixed minutes or 16.1 degrees, and which of them defines the temporal hours?",
      },
      {
        he: "שאר הזמנים בפרופיל זה נלקחים מהגר״א. האם זה נכון, או שגם הם צריכים להימנות לפי מג״א?",
        en: "The other zmanim here follow the GRA. Is that right, or should they follow the Magen Avraham day too?",
      },
    ],
    opinions: {
      alos: "alos.min_72",
      misheyakir: "misheyakir.deg_11",
      sunrise: "sunrise.sea_level",
      sof_zman_shma: "sof_zman_shma.mga_72",
      sof_zman_tfila: "sof_zman_tfila.mga_72",
      chatzot: "chatzot.astronomical",
      mincha_gedola: "mincha_gedola.gra_greater_than_30",
      mincha_ketana: "mincha_ketana.gra",
      plag_hamincha: "plag_hamincha.gra",
      sunset: "sunset.sea_level",
      tzeit: "tzeit.deg_8_5",
      tzeit_rt: "tzeit_rt.min_72",
      chatzot_night: "chatzot_night.half_night",
      sof_zman_achilat_chametz: "sof_zman_achilat_chametz.mga_72",
      sof_zman_biur_chametz: "sof_zman_biur_chametz.mga_72",
    },
  },
  {
    id: "baal_hatanya",
    label: { he: 'אדמו"ר הזקן (חב"ד)', en: "Baal HaTanya (Chabad)" },
    description: {
      he: 'שיטת אדמו"ר הזקן: שעות זמניות מהנץ האמיתי עד השקיעה האמיתית (1.583°), עלות השחר 16.9°, צאת הכוכבים 6°.',
      en: "The Alter Rebbe's method: temporal hours from true sunrise to true sunset (1.583°), dawn at 16.9°, nightfall at 6°.",
    },
    status: "draft",
    notes: {
      he: 'זה אינו לוח הזמנים שמפרסמים חב"ד בישראל, שבו השעות הזמניות נמנות מהנץ עד השקיעה — לשם כך יש לבחור "לוח חב"ד בישראל". משיכיר ור"ת לפי שיטה זו טרם אומתו.',
      en: 'This is not the zmanim table published by Chabad in Israel, whose temporal hours run from sunrise to sunset; for that, choose "Chabad Israel luach". Misheyakir and Rabbeinu Tam for this method are not yet verified.',
    },
    openQuestions: [
      {
        he: "מול איזה לוח חב״ד צריך הפרופיל להתאים? הלוח שמפרסמים חב״ד בישראל אינו מחושב בשיטה זו, ולו יש פרופיל נפרד.",
        en: "Which Chabad luach should this profile match? The one published by Chabad in Israel is not computed this way and has a profile of its own.",
      },
      {
        he: "משיכיר וצאת הכוכבים לרבנו תם לפי שיטת אדמו״ר הזקן טרם אומתו.",
        en: "Misheyakir and Rabbeinu Tam according to the Alter Rebbe's method are not verified.",
      },
    ],
    opinions: {
      alos: "alos.deg_16_9",
      misheyakir: "misheyakir.deg_11",
      sunrise: "sunrise.sea_level",
      sof_zman_shma: "sof_zman_shma.baal_hatanya",
      sof_zman_tfila: "sof_zman_tfila.baal_hatanya",
      chatzot: "chatzot.astronomical",
      mincha_gedola: "mincha_gedola.baal_hatanya",
      mincha_ketana: "mincha_ketana.baal_hatanya",
      plag_hamincha: "plag_hamincha.baal_hatanya",
      sunset: "sunset.sea_level",
      tzeit: "tzeit.deg_6",
      tzeit_rt: "tzeit_rt.min_72",
      chatzot_night: "chatzot_night.half_night",
      sof_zman_achilat_chametz: "sof_zman_achilat_chametz.baal_hatanya",
      sof_zman_biur_chametz: "sof_zman_biur_chametz.baal_hatanya",
    },
  },
  {
    id: "chabad_luach",
    label: { he: 'לוח חב"ד בישראל', en: "Chabad Israel luach" },
    description: {
      he: 'לפי לוח הזמנים שמפרסמים חב"ד בישראל: שעות זמניות מהנץ עד השקיעה, עלות השחר 16°, טלית ותפילין 11.5°, צאת הכוכבים 5.88°, וסוף זמן קריאת שמע מוקדם בשתי דקות.',
      en: "Following the zmanim table published by Chabad in Israel: temporal hours from sunrise to sunset, dawn at 16°, tallit and tefillin at 11.5°, nightfall at 5.88°, and the latest Shema two minutes earlier.",
    },
    status: "draft",
    notes: {
      he: 'זה אינו הפרופיל של אדמו"ר הזקן: בלוח הזה השעות הזמניות נמנות מהנץ עד השקיעה, ולא מהנץ האמיתי עד השקיעה האמיתית. צאת הכוכבים לרבנו תם, חצות הלילה וזמני ערב פסח כאן אינם מופיעים בלוח שלהם וטרם אומתו.',
      en: "This is not the Baal HaTanya profile: here temporal hours run from sunrise to sunset, not from true sunrise to true sunset. Rabbeinu Tam, midnight and the erev Pesach times do not appear in their table and are not verified.",
    },
    openQuestions: [
      {
        he: "הלוח שלהם אינו מציג צאת הכוכבים לרבנו תם, חצות הלילה וזמני ערב פסח. אצלנו הם מחושבים לפי הגר״א, ללא מקור.",
        en: "Their table does not show Rabbeinu Tam, midnight or the erev Pesach times. Here they follow the GRA, with no source.",
      },
      {
        he: "הלוח שלהם מעגל לדקה הקרובה, ואצלנו העיגול לחומרא. האם להתאים?",
        en: "Their table rounds to the nearest minute; here the rounding is stringent. Should it match?",
      },
    ],
    opinions: {
      alos: "alos.deg_15_99",
      misheyakir: "misheyakir.deg_11_5",
      sunrise: "sunrise.sea_level",
      sof_zman_shma: "sof_zman_shma.chabad_luach",
      sof_zman_tfila: "sof_zman_tfila.gra",
      chatzot: "chatzot.astronomical",
      mincha_gedola: "mincha_gedola.gra_greater_than_30",
      mincha_ketana: "mincha_ketana.gra",
      plag_hamincha: "plag_hamincha.gra",
      sunset: "sunset.sea_level",
      tzeit: "tzeit.deg_5_88",
      tzeit_rt: "tzeit_rt.min_72",
      chatzot_night: "chatzot_night.half_night",
      sof_zman_achilat_chametz: "sof_zman_achilat_chametz.gra",
      sof_zman_biur_chametz: "sof_zman_biur_chametz.gra",
    },
  },
  {
    id: "sephardi",
    label: { he: "ספרדי (אור החיים)", en: "Sephardi (Or HaChaim)" },
    description: {
      he: 'לפי הלוחות ההולכים בשיטת הגר"ע יוסף: דקות זמניות, שקיעה לפי גובה.',
      en: "Following luachot based on Rav Ovadia Yosef: temporal minutes, elevation-adjusted sunset.",
    },
    status: "partial",
    notes: {
      he: "חלקי: לוחות אלה משתמשים בנץ הנראה (לפי ההרים), שעדיין אינו מחושב באתר, ולכן הנץ כאן מישורי. שאר הזמנים הושוו להגדרות הכתובות של לוח אור החיים; צאת השבת, הדלקת הנרות ופלג המנחה עדיין שונים בו מהאתר.",
      en: "Partial: these luachot use visible sunrise (terrain-based), which is not computed yet, so sunrise here is sea level. The other zmanim were compared against the published Or HaChaim definitions; Shabbat's end, candle lighting and plag still differ there from the site.",
    },
    openQuestions: [
      {
        he: "הלוח נוקט פלג המנחה 1.025 שעות זמניות לפני השקיעה, והמקור מעיר בעצמו שזו כנראה טעות וצריך להיות 1.25 — כפי שאצלנו. מה הנכון?",
        en: "The luach gives plag as 1.025 temporal hours before sunset, and the source suspects its own figure is a typo for 1.25, which is what is used here. Which is right?",
      },
      {
        he: "הלוח נוקט מוצאי שבת 30 דקות קבועות אחרי השקיעה והדלקת נרות 20 דקות, ואצלנו צאת השבת היא 8.5° וההדלקה לפי מנהג היישוב.",
        en: "The luach ends Shabbat 30 fixed minutes after sunset and lights candles at 20 minutes; here Shabbat ends at 8.5 degrees and candles follow the local custom.",
      },
      {
        he: "ההגדרות של הלוח מבחינות בין ״הזריחה האסטרונומית״ ל״הזריחה המישורית״ בלי להסביר. הנחנו שהכוונה למישורית.",
        en: "The luach's definitions distinguish astronomical sunrise from sea-level sunrise without explaining; sea level was assumed.",
      },
      {
        he: "הלוח משתמש בנץ הנראה לפי ההרים, שאינו מחושב באתר.",
        en: "The luach uses visible sunrise based on the terrain, which the site does not compute.",
      },
    ],
    opinions: {
      alos: "alos.zmaniyot_72",
      misheyakir: "misheyakir.zmaniyot_66",
      sunrise: "sunrise.sea_level",
      sof_zman_shma: "sof_zman_shma.mga_72_zmaniyot",
      sof_zman_tfila: "sof_zman_tfila.gra",
      chatzot: "chatzot.astronomical",
      mincha_gedola: "mincha_gedola.min_30",
      mincha_ketana: "mincha_ketana.gra",
      plag_hamincha: "plag_hamincha.gra",
      sunset: "sunset.elevation",
      tzeit: "tzeit.zmaniyot_13_5",
      tzeit_rt: "tzeit_rt.zmaniyot_72",
      chatzot_night: "chatzot_night.half_night",
      sof_zman_achilat_chametz: "sof_zman_achilat_chametz.mga_72_zmaniyot",
      sof_zman_biur_chametz: "sof_zman_biur_chametz.mga_72_zmaniyot",
    },
  },
  {
    id: "luach_eretz_israel",
    label: { he: "לוח ארץ ישראל", en: "Luach Eretz Yisrael" },
    description: {
      he: "בעקבות לוח ארץ ישראל של ר' יחיאל מיכל טוקצ'ינסקי: צאת הכוכבים 6.45°, שקיעה לפי גובה.",
      en: "Following R. Tukachinsky's Luach Eretz Yisrael: nightfall at 6.45°, elevation-adjusted sunset.",
    },
    status: "partial",
    notes: {
      he: "חלקי: הלוח משתמש בנץ הנראה, שעדיין אינו מחושב באתר. שאר הזמנים תואמים להגדרות הכתובות של השיטה האשכנזית הרווחת, חוץ מצאת הכוכבים בימות החול, שאין בו מנהג אחיד.",
      en: "Partial: the luach uses visible sunrise, which is not computed yet. The other zmanim match the published definitions of the common Ashkenazi method, except weekday nightfall, where there is no settled custom.",
    },
    openQuestions: [
      {
        he: "מה נוקט הלוח לצאת הכוכבים בימות החול? מחשבי הלוחות כותבים שאין בזה מנהג אשכנזי ברור, ושהערך שבפרופיל (6.45°) הוא אצלם מוצאי צום.",
        en: "What does the luach give for weekday nightfall? The calculators write that there is no settled Ashkenazi custom, and that the value used here (6.45 degrees) is their end of a fast.",
      },
      {
        he: "עלות השחר בפרופיל היא 16.1°, ומחשבי הלוחות נוקטים 16.013°. ההפרש עד 0.6 דקות.",
        en: "Dawn here is 16.1 degrees where the calculators give 16.013; the difference reaches 0.6 minutes.",
      },
      {
        he: "הלוח משתמש בנץ הנראה לפי ההרים, שאינו מחושב באתר.",
        en: "The luach uses visible sunrise based on the terrain, which the site does not compute.",
      },
    ],
    opinions: {
      alos: "alos.deg_16_1",
      misheyakir: "misheyakir.deg_11_5",
      sunrise: "sunrise.sea_level",
      sof_zman_shma: "sof_zman_shma.gra",
      sof_zman_tfila: "sof_zman_tfila.gra",
      chatzot: "chatzot.astronomical",
      mincha_gedola: "mincha_gedola.gra_greater_than_30",
      mincha_ketana: "mincha_ketana.gra",
      plag_hamincha: "plag_hamincha.gra",
      sunset: "sunset.elevation",
      tzeit: "tzeit.deg_6_45",
      tzeit_rt: "tzeit_rt.min_72",
      chatzot_night: "chatzot_night.half_night",
      sof_zman_achilat_chametz: "sof_zman_achilat_chametz.gra",
      sof_zman_biur_chametz: "sof_zman_biur_chametz.gra",
    },
  },
];

export const DEFAULT_PROFILE_ID = "gra";
